import autoBind from "auto-bind";

import { SponsoredPostData } from "../body/desktop-body.component/right-sidebar/right-sidebar.component";
import { Author } from "../manifest/models/author.models";
import { GoalFeedback } from "../manifest/models/goal.feedback";
import { Shortcut } from "../manifest/models/sidebar.models";
import {
  FollowupAction,
  GoalFeedbackPost,
  PostData,
  YoutubeVideoPost,
} from "../posts/post.models";
import { ShuffledCyclingBank } from "../utils/collections/shuffled_cycling_bank";
import { dumpCSV } from "../utils/csv_dump";
import { randomNumber } from "../utils/randomization";
import { getUsernameFor } from "../utils/username";
import {
  FeedGenerationStrategy,
  FeedSequenceItem,
  GenerationConstraint,
  SequenceType,
} from "./generation.models";
import { StimuliBank } from "./stimuli.bank";
import { AuthorGenerator } from "./user_data/author.generator";
import { CommentGenerator, commentToId } from "./user_data/comment.generator";
import { PostDateGenerator } from "./user_data/date.generator";
import { InteractionGenerator } from "./user_data/interaction.generator";

export class FeedGenerator {
  private strategy: FeedGenerationStrategy;
  private authorGenerator!: AuthorGenerator;
  private postDateGenerator!: PostDateGenerator;
  private commentGenerator!: CommentGenerator;
  private likeGenerator!: InteractionGenerator;
  private dislikeGenerator!: InteractionGenerator;
  private shareGenerator!: InteractionGenerator;

  private shortcutBank!: ShuffledCyclingBank<Shortcut>;

  private stimuli: StimuliBank;

  private articles: PostData[] = [];
  private advertisements: SponsoredPostData[] = [];
  private infographics: PostData[] = [];
  private neutralInfographics: PostData[] = [];
  private memes: PostData[] = [];
  private quizzes: PostData[] = [];
  private posts: PostData[] = [];
  private videos: YoutubeVideoPost[] = [];
  private filler: PostData[] = [];
  private friendPosts: PostData[] = [];

  private currentArticle = 0;
  private currentAdvertisement = 0;
  private currentInfographic = 0;
  private currentNeutralInfographic = 0;
  private currentMeme = 0;
  private currentQuiz = 0;
  private currentVideo = 0;
  private currentFiller = 0;
  private currentFriendPost = 0;
  private stage: number | undefined;

  constructor(stimuli: StimuliBank, stage?: number) {
    autoBind(this);
    this.stage = stage;
    this.stimuli = stimuli;
    this.strategy = this.stimuli.generationStrategy;
  }

  async generate(): Promise<PostData[]> {
    this.authorGenerator = new AuthorGenerator(this.stimuli.authors);
    this.shortcutBank = new ShuffledCyclingBank(
      this.stimuli.sidebar?.shortcuts ?? [],
    );
    this.commentGenerator = new CommentGenerator(
      this.stimuli.comments,
      this.stimuli.commentStrategy,
      this.stimuli.condition!.id!,
      this.authorGenerator,
    );
    this.likeGenerator = new InteractionGenerator(
      this.stimuli.likeStrategy,
      "likes",
    );
    this.dislikeGenerator = new InteractionGenerator(
      this.stimuli.dislikeStrategy,
      "dislikes",
    );
    this.shareGenerator = new InteractionGenerator(
      this.stimuli.shareStrategy,
      "shares",
    );
    this.postDateGenerator = new PostDateGenerator(this.stimuli.dateStrategy);
    this.memes = this.stimuli.memes.slice();
    this.articles = this.stimuli.articles.slice();
    this.advertisements = this.stimuli.advertisements.slice();
    this.infographics = this.stimuli.infographics.slice();
    this.neutralInfographics = this.stimuli.neutralInfographics.slice() ?? [];
    this.quizzes = this.stimuli.quizzes?.slice() ?? [];
    this.videos = this.stimuli.videos?.slice() ?? [];
    this.filler = this.stimuli.filler?.slice() ?? [];
    this.friendPosts = this.stimuli.friendPosts?.slice() ?? [];
    const goalFeedback = await this.generateGoalFeedback();

    if (
      this.strategy.appearance_order == null ||
      this.strategy.appearance_order.length == 0
    ) {
      /// If we weren't given an appearance order, just show them in line.
      return [
        ...(goalFeedback != null ? [goalFeedback] : []),
        ...this.articles,
        ...this.advertisements,
        ...this.infographics,
        ...this.memes,
        ...this.videos,
        ...this.quizzes,
      ];
    }

    let done = false;
    let index = 0;
    let iteration = 0;
    let maxIterations = 1000;

    while (!done) {
      iteration++;

      if (iteration >= maxIterations) {
        throw new Error(
          `Failed to complete feed generation after ${iteration} iterations! You likely have an issue with your constraints`,
        );
      }

      const item =
        this.strategy.appearance_order[
          index % this.strategy.appearance_order.length
        ];

      done = this.processSequenceItem(item);
      index++;
    }

    this.posts = this.postDateGenerator.addPostDates(this.posts);
    this.posts = this.authorGenerator.addPostAuthors(this.posts);
    this.posts = this.commentGenerator.addPostComments(this.posts);

    if (goalFeedback != null) {
      this.posts = [goalFeedback, ...this.posts];
    }

    this.posts = this.likeGenerator.addPostInteractions(this.posts);
    this.posts = this.dislikeGenerator.addPostInteractions(this.posts);
    this.posts = this.shareGenerator.addPostInteractions(this.posts);

    console.debug(
      "feed_generation_stats",
      dumpCSV([
        [
          "post-type",
          "post-id",
          "like-count",
          "share-count",
          "comment-count",
          "author",
          "post-date",
        ],
        ...this.posts.map((post) => {
          return [
            post.contentType,
            post.id,
            post.likes,
            post.shares,
            post.comments?.length,
            post.author,
            post.date,
          ];
        }),
      ]),
    );

    return this.posts;
  }

  async generateGoalFeedback(): Promise<GoalFeedbackPost | undefined> {
    if (this.stimuli.goal_feedback == null) {
      console.debug("experiment has no goal feedback configured, skipping");
      return;
    }
    const feedback: GoalFeedback = this.stimuli.goal_feedback!;
    if (
      this.stage != null &&
      feedback.stage != null &&
      this.stage != feedback.stage
    ) {
      console.debug(
        "skipping goal feedback because feedback stage was not specified or the experiment stage is not configured.",
        "this.stage = ",
        this.stage,
        "feedback.stage = ",
        feedback.stage,
      );
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (!params.has("d")) return;

    const goalContent: string = decodeURIComponent(params.get("d")!);
    console.debug("goal content = ", goalContent);
    const goalUser = this.stimuli.user!;

    const post: GoalFeedbackPost = {
      goals: goalContent,
      id: "goal-feedback-post",
      type: "goal-feedback",
      date: feedback.post_date,
      author: getUsernameFor(goalUser),
      avatar: goalUser.avatar.filename,
      comments: feedback.feedback_content.map((comment) => {
        const feedbackUser = this.grabInteractionAuthor();
        return {
          id: commentToId(comment),
          body: comment,
          date: feedback.post_date,
          author: getUsernameFor(feedbackUser),
          avatar: feedbackUser.avatar.filename,
        };
      }),
      userFollowup: {
        action: FollowupAction.like,
        delay: randomNumber(15, 45),
        user: this.grabInteractionAuthor(),
        metadata: {},
      },
    };
    console.debug("generated goal feedback post", post);
    return post;
  }

  grabInteractionAuthor(): Author {
    const user = this.authorGenerator.getNextObject();
    const strategy = this.stimuli.commentStrategy.comment_followup;
    if (!strategy?.enabled) return user;
    if (strategy.usernameConditionOverrides == null) return user;
    const override: string | undefined =
      strategy.usernameConditionOverrides[this.stimuli.condition!.id!];
    if (override == null) return user;
    return {
      ...user,
      first: override,
      last: "",
    };
  }

  processSequenceItem(item: FeedSequenceItem): boolean {
    if (this.validateConstraints(this.allConstraints())) {
      return true;
    }

    const type = item.type;
    const min = item.amount.min;
    const max = item.amount.max;
    const numberToAdd = Math.min(Math.trunc(Math.random() * (max - min) + min));

    for (let i = 0; i < numberToAdd; i++) {
      if (!this.shouldSkip(this.getConstraintFor(type))) {
        const post = this.getNextPostFor(type);
        this.posts.push(post);
      }
    }

    if (this.validateConstraints(this.allConstraints())) {
      return true;
    }

    return false;
  }

  allConstraints(): GenerationConstraint[] {
    return [
      this.getConstraintFor("advertisements"),
      this.getConstraintFor("articles"),
      this.getConstraintFor("memes"),
      this.getConstraintFor("infographics"),
      this.getConstraintFor("neutral-infographics"),
      this.getConstraintFor("quizzes"),
      this.getConstraintFor("videos"),
      this.getConstraintFor("filler"),
      this.getConstraintFor("friend-post"),
    ].filter(
      (constraint): constraint is GenerationConstraint => constraint != null,
    );
  }

  getConstraintFor(type: SequenceType): GenerationConstraint | undefined {
    let constraint;
    switch (type) {
      case "memes":
        constraint = this.strategy.constraints?.memes;
        break;
      case "advertisements":
        constraint = this.strategy.constraints?.advertisements;
        break;
      case "articles":
        constraint = this.strategy.constraints?.articles;
        break;
      case "infographics":
        constraint = this.strategy.constraints?.infographics;
        break;
      case "neutral-infographics":
        constraint = this.strategy.constraints?.neutral_infographics;
        break;
      case "quizzes":
        constraint = this.strategy.constraints?.quiz;
        break;
      case "videos":
        constraint = this.strategy.constraints?.videos;
        break;
      case "filler":
        constraint = this.strategy.constraints?.filler;
        break;
      case "friend-post":
        constraint = this.strategy.constraints?.friend_posts;
        break;
      default:
        throw new Error(`Unknown appearance order type ${type}`);
    }

    return {
      ...constraint,
      type: type,
    };
  }

  getNextPostFor(type: SequenceType): PostData {
    switch (type) {
      case "memes":
        return {
          ...this.memes[this.currentMeme++ % this.memes.length],
          contentType: "meme",
        };
      case "advertisements":
        return {
          ...this.advertisements[
            this.currentAdvertisement++ % this.advertisements.length
          ],
          contentType: "advertisement",
        };
      case "articles":
        return {
          ...this.articles[this.currentArticle++ % this.articles.length],
          contentType: "article",
        };
      case "infographics":
        return {
          ...this.infographics[
            this.currentInfographic++ % this.infographics.length
          ],
          contentType: "infographic",
        };
      case "neutral-infographics":
        return {
          ...this.neutralInfographics[
            this.currentNeutralInfographic++ % this.neutralInfographics.length
          ],
          contentType: "neutral-infographic",
        };
      case "quizzes":
        return {
          ...this.quizzes[this.currentQuiz++ % this.quizzes.length],
          contentType: "quiz",
        };
      case "videos":
        return {
          ...this.videos[this.currentVideo++ % this.videos.length],
          contentType: "video",
        };
      case "filler":
        return {
          ...this.filler[this.currentFiller++ % this.filler.length],
          contentType: "filler",
        };
      case "friend-post":
        return {
          ...this.friendPosts[
            this.currentFriendPost++ % this.friendPosts.length
          ],
          contentType: "friend-post",
        };
      default:
        throw new Error(`Unknown appearance order type ${type}`);
    }
  }

  validateConstraints(constraints: GenerationConstraint[]): boolean {
    return (
      constraints.filter(this.validateConstraint).length === constraints.length
    );
  }

  getItemsFor(type: SequenceType): { items: PostData[]; currentIndex: number } {
    let items;
    let currentIndex;

    switch (type) {
      case "memes":
        items = this.memes;
        currentIndex = this.currentMeme;
        break;
      case "advertisements":
        items = this.advertisements;
        currentIndex = this.currentAdvertisement;
        break;
      case "articles":
        items = this.articles;
        currentIndex = this.currentArticle;
        break;
      case "infographics":
        items = this.infographics;
        currentIndex = this.currentInfographic;
        break;
      case "neutral-infographics":
        items = this.neutralInfographics;
        currentIndex = this.currentNeutralInfographic;
        break;
      case "quizzes":
        items = this.quizzes;
        currentIndex = this.currentQuiz;
        break;
      case "videos":
        items = this.videos;
        currentIndex = this.currentVideo;
        break;
      case "filler":
        items = this.filler;
        currentIndex = this.currentFiller;
        break;
      case "friend-post":
        items = this.friendPosts;
        currentIndex = this.currentFriendPost;
    }

    return {
      items: items,
      currentIndex: currentIndex,
    };
  }

  /**
   * Determine whether we should skip generating for a given sequence type.
   * Currently the only way this happens is if allow_repeats is false and we've exhausted all the options.
   * This logic is separated from validateConstraint because a constraint that is use_all false, but allow_repeats false
   * needs to flag to skip generating once we've exhausted, but validate true always.
   * @param type The sequence type to check.
   * @returns True if we should skip generating this sequence type on this iteration.
   */
  shouldSkip(constraint: GenerationConstraint | undefined): boolean {
    if (constraint == null) return false;
    if (constraint.type == null) {
      throw new Error(
        "Unable to validate a constraint that doesn't have sequence type attached!",
      );
    }

    let { items, currentIndex } = this.getItemsFor(constraint.type);

    if (!constraint.allow_repeats && currentIndex >= items.length) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Validate the given constraint to determine if it has been satisfied by the generation algorithm so far.
   * We keep iterating until all constraints validate.
   */
  validateConstraint(constraint: GenerationConstraint | undefined): boolean {
    if (constraint == null) return false;
    if (constraint.type == null) {
      throw new Error(
        "Unable to validate a constraint that doesn't have sequence type attached!",
      );
    }

    let { items, currentIndex } = this.getItemsFor(constraint.type);

    if (constraint.use_all) {
      return currentIndex >= items.length;
    } else {
      return true;
    }
  }

  chooseSponsoredAds(): SponsoredPostData {
    /// Since we've shuffled the advertisements, we can just take the first index for a "random" ad.
    return this.advertisements[0];
    /// Remove the chosen ad from the list of ads.
    /// TODO: Do we want to remove it or dupe it?
    // this.advertisements = this.advertisements.slice(1);
  }

  chooseBirthdays(): Author[] {
    return [
      this.authorGenerator.getNextObject(),
      this.authorGenerator.getNextObject(),
    ];
  }

  chooseContactList(): Author[] {
    const numberOfContacts = randomNumber(5, 10);
    const contacts = [];

    for (let i = 0; i < numberOfContacts; i++) {
      contacts.push({
        ...this.authorGenerator.getNextObject(),
      });
    }

    return contacts;
  }

  chooseShortcuts(): Shortcut[] {
    if (this.shortcutBank.isEmpty()) return [];

    const numberOfShortcuts = 5;
    const shortcuts = [];

    for (let i = 0; i < numberOfShortcuts; i++) {
      shortcuts.push(this.shortcutBank.getNextObject());
    }

    return shortcuts;
  }

  get name() {
    return this.stimuli.name;
  }

  get instructions() {
    return this.stimuli.instructions;
  }

  get contact() {
    return this.stimuli.contact;
  }
}
